import { AnimatePresence, motion } from "framer-motion";

import { Button } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn, useIsOpen } from "@virtual-brain-lib/frontend-design-system/utils";
import type { MessageToolDocument } from "@virtual-brain-lib/types";

import { DocumentRow } from "./DocumentRow";
import { ToolCardAllDocumentsModal } from "./ToolCardAllDocumentsModal";

const MAX_VISIBLE = 5;

interface ToolCardDocumentListProps {
	readonly documents: readonly MessageToolDocument[];
	readonly contentReferenceOffset?: number;
	readonly onDocumentClick?: (doc: MessageToolDocument) => void;
	readonly className?: string;
}

export function ToolCardDocumentList({
	documents,
	contentReferenceOffset,
	onDocumentClick,
	className,
}: ToolCardDocumentListProps) {
	const modal = useIsOpen();

	if (documents.length === 0) {
		return null;
	}

	const visible = documents.slice(0, MAX_VISIBLE);
	const hiddenCount = documents.length - visible.length;

	return (
		<div className={cn("flex flex-col gap-2", className)}>
			<div className="flex flex-col">
				<AnimatePresence initial={false}>
					{visible.map((doc) => {
						const hasAction = Boolean(doc.url) || Boolean(onDocumentClick);

						return (
							<motion.div
								key={doc.id}
								animate={{ opacity: 1 }}
								exit={{ opacity: 0 }}
								initial={{ opacity: 0 }}
								transition={{ duration: 0.2 }}
							>
								<DocumentRow
									document={doc}
									onDocumentClick={
										hasAction
											? (doc) => {
													if (doc.url) {
														window.open(doc.url, "_blank");
													} else {
														onDocumentClick?.(doc);
													}
												}
											: undefined
									}
								/>
							</motion.div>
						);
					})}
				</AnimatePresence>
			</div>

			{hiddenCount > 0 && (
				<Button
					classNames={{ wrapper: "-ml-1.5 self-start" }}
					color="secondary"
					size="xs"
					variant="ghost"
					onClick={modal.open}
				>
					Show all
				</Button>
			)}

			<ToolCardAllDocumentsModal
				close={modal.close}
				documents={documents}
				contentReferenceOffset={contentReferenceOffset}
				isOpen={modal.isOpen}
				onDocumentClick={onDocumentClick}
			/>
		</div>
	);
}
